import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
}

/**
 * Red de seguridad para errores de render.
 *
 * Sin esto, un fallo en cualquier componente deja la pantalla en blanco y sin
 * salida. En una PWA instalada eso es especialmente malo: no hay barra de
 * direcciones a la vista, así que la única opción sería borrar la app y volver
 * a añadirla, lo que además tiraría la suscripción de notificaciones.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Error de render', error, info.componentStack)
  }

  render() {
    if (!this.state.error) return this.props.children

    return (
      <div className="mx-auto flex min-h-dvh max-w-sm flex-col justify-center px-6 text-center">
        <h1 className="mb-2 text-xl font-bold">Algo ha fallado</h1>
        <p className="mb-6 text-sm text-[var(--color-muted)]">
          La app no ha podido dibujar esta pantalla. Vuelve a intentarlo; si sigue pasando,
          avisa en casa.
        </p>
        <button
          onClick={() => {
            this.setState({ error: null })
            window.location.href = '/'
          }}
          className="min-h-11 rounded-xl bg-[var(--color-brand)] px-4 font-semibold text-white"
        >
          Volver al inicio
        </button>
        <pre className="mt-6 overflow-x-auto text-left text-[11px] text-[var(--color-muted)]">
          {this.state.error.message}
        </pre>
      </div>
    )
  }
}
